import { useId, useState, type ReactNode } from "react";
import { useAuth } from "../auth/index.ts";
import { ApiError } from "../lib/api.ts";

/**
 * The team switcher (design 12.2).
 *
 * Scope rule: switching teams is for operators who ADMINISTER more than one team. So the dropdown
 * is shown ONLY when the signed-in user is an `admin` of 2+ teams, and it lists ONLY the teams they
 * administer (switching into a team you merely view/operate is not offered — and the server refuses
 * it). Everyone else — a single-team user, or a multi-team user who is not an admin of 2+ of them —
 * sees a static "Team: <name>" label with no switching affordance.
 *
 * Selecting a team is a real SERVER round-trip (`useAuth().switchTeam` → `POST /auth/switch-team`):
 * the server rotates the session, re-resolves `(tid, role)`, and the auth context re-reads, so every
 * screen (and the BFF's `/api/*` scoping) follows the server's new selection. The id submitted is
 * the tenant id; "team" is the user-facing name for a tenant.
 *
 * The list offers only the active, non-banned teams the server returned (the server's team list
 * already excludes banned rows — per-team-signin-factors R13.7). A refused switch (e.g. the target
 * just became banned, so the server returns not-authorized and leaves `selected_tenant` UNCHANGED —
 * R13.8) leaves the CURRENT team selected: we never mutate the selection client-side, and
 * `AuthContext` re-reads the session on the refusal to reconcile the team list from the server.
 */
export function TenantSwitcher(): ReactNode {
  const auth = useAuth();
  const selectId = useId();
  const [error, setError] = useState<string | null>(null);

  const identity = auth.identity;
  if (identity === null) return null;

  const selectedName = (identity.team?.name ?? "").trim() || identity.team?.id || "—";
  // Only the teams the user ADMINISTERS are switch targets.
  const adminTeams = identity.teams.filter((t) => t.role === "admin");

  // The dropdown is offered only to an admin of 2+ teams; otherwise show a static label.
  if (adminTeams.length < 2) {
    return (
      <span className="control">
        <span className="control__label">Team</span>
        <span className="control__static" title={selectedName}>{selectedName}</span>
      </span>
    );
  }

  const selected = identity.team?.id ?? "";

  const onChange = async (nextId: string): Promise<void> => {
    if (nextId === "" || nextId === selected) return;
    setError(null);
    try {
      await auth.switchTeam(nextId);
    } catch (e) {
      setError(
        e instanceof ApiError && e.kind === "forbidden"
          ? "You can only switch between teams you administer."
          : "Could not switch team. Please try again.",
      );
    }
  };

  return (
    <span className="control">
      <label className="control__label" htmlFor={selectId}>
        Team
      </label>
      <select
        id={selectId}
        className="control__select"
        value={selected}
        disabled={auth.switchingTeam}
        {...(error === null ? {} : { title: error })}
        aria-busy={auth.switchingTeam}
        onChange={(event) => void onChange(event.target.value)}
      >
        {selected === "" && (
          <option value="" disabled>
            {auth.switchingTeam ? "Switching…" : "Select team"}
          </option>
        )}
        {adminTeams.map((t) => (
          <option key={t.id} value={t.id}>
            {(t.name ?? "").trim() || t.id}
          </option>
        ))}
      </select>
    </span>
  );
}
